// src/features/auth/route-protection/components/PublicRoute.tsx
import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppSelector } from '@/app/hooks';
import { tenantPathSegment } from '@/features/auth/utils/tenantPath';

interface PublicRouteProps {
  restrictedPath?: string; // Where to redirect if already authenticated
}

// Credential / token-bearing self-service flows must render even when a session
// is already active on this browser: the person clicking a reset or invite link
// may not be whoever is currently logged in, and the TOKEN — not the session — is
// the identity. Redirecting these to the active dashboard both hides the form and
// can drop the visitor into the wrong account (e.g. a reset for one email landing
// in another account's session). See [[me-identity-split-brain-fix]].
const isTokenFlow = (pathname: string): boolean =>
  pathname.endsWith('/reset-password') ||
  pathname.endsWith('/forgot-password') ||
  pathname.includes('/invite/');

export const PublicRoute: React.FC<PublicRouteProps> = ({
  restrictedPath
}) => {
  const { isAuthenticated, isInitialized, user } = useAppSelector(state => state.auth);
  const { current: currentTenant } = useAppSelector(state => state.tenant);
  const location = useLocation();

  // Don't redirect until auth is initialized
  if (!isInitialized) {
    return <Outlet />;
  }

  // If user is already authenticated, redirect to dashboard or specified path —
  // EXCEPT for token flows (password reset / invite), which must always render.
  if (isAuthenticated && !isTokenFlow(location.pathname)) {
    // If a specific path is provided, use it
    if (restrictedPath) {
      return <Navigate to={restrictedPath} replace />;
    }

    // Otherwise, build the tenant-aware dashboard path
    const tenantSlug = tenantPathSegment(currentTenant);
    const defaultPath = tenantSlug ? `/dashboard/${tenantSlug}/dashboard` : '/dashboard';

    return <Navigate to={defaultPath} replace />;
  }

  // Render child routes
  return <Outlet />;
};

export default PublicRoute;